import React, { useEffect, useRef, useState } from 'react';
import {
  Move,
  Camera,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUpLeft,
  ArrowUpRight,
  ArrowDownLeft,
  ArrowDownRight,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { moveRotate, getSnapshot } from '../api/ropApi';

const GENERIC_ERROR_MESSAGE = 'No se pudo enviar el comando a la cámara.';

const getErrorMessage = (error) => error?.response?.data?.message || GENERIC_ERROR_MESSAGE;

const DIRECTIONS = [
  { key: 'leftUp', label: 'Mover en diagonal hacia arriba a la izquierda', icon: ArrowUpLeft },
  { key: 'up', label: 'Mover hacia arriba', icon: ArrowUp },
  { key: 'rightUp', label: 'Mover en diagonal hacia arriba a la derecha', icon: ArrowUpRight },
  { key: 'left', label: 'Mover hacia la izquierda', icon: ArrowLeft },
  null,
  { key: 'right', label: 'Mover hacia la derecha', icon: ArrowRight },
  { key: 'leftDown', label: 'Mover en diagonal hacia abajo a la izquierda', icon: ArrowDownLeft },
  { key: 'down', label: 'Mover hacia abajo', icon: ArrowDown },
  { key: 'rightDown', label: 'Mover en diagonal hacia abajo a la derecha', icon: ArrowDownRight },
];

const RopMoverTab = ({ uid }) => {
  const [speed, setSpeed] = useState(32);
  // Tracks which direction button currently has an in-flight "start moving" press,
  // so pointerLeave only sends a stop command when a press actually happened.
  const pressedDirectionRef = useRef(null);

  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshotUrl, setSnapshotUrl] = useState(null);
  const snapshotUrlRef = useRef(null);

  const fetchSnapshot = async () => {
    if (!uid) return;
    setSnapshotLoading(true);
    try {
      const blob = await getSnapshot(uid, {});
      const url = URL.createObjectURL(blob);
      if (snapshotUrlRef.current) {
        URL.revokeObjectURL(snapshotUrlRef.current);
      }
      snapshotUrlRef.current = url;
      setSnapshotUrl(url);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSnapshotLoading(false);
    }
  };

  useEffect(() => {
    fetchSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  // Revoke the last snapshot object URL on unmount to avoid leaking memory.
  useEffect(() => {
    return () => {
      if (snapshotUrlRef.current) {
        URL.revokeObjectURL(snapshotUrlRef.current);
      }
    };
  }, []);

  if (!uid) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <p className="text-sm text-gray-600 text-center">
          Esta cámara no tiene un dispositivo ROP vinculado.
        </p>
      </div>
    );
  }

  const sendRotate = async (direction) => {
    try {
      await moveRotate(uid, { direction, speed });
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handlePointerDown = (direction) => {
    pressedDirectionRef.current = direction;
    sendRotate(direction);
  };

  const handlePointerStop = (direction) => {
    if (pressedDirectionRef.current !== direction) return;
    pressedDirectionRef.current = null;
    sendRotate('none');
    fetchSnapshot();
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center">
            <Camera size={18} />
          </div>
          <h3 className="text-base font-semibold text-gray-800">Vista actual</h3>
        </div>
        {snapshotLoading && !snapshotUrl && (
          <p className="text-sm text-gray-600 text-center py-6">Cargando snapshot…</p>
        )}
        {snapshotUrl && (
          <img
            src={snapshotUrl}
            alt="Snapshot actual de la cámara"
            className={`w-full aspect-video object-cover rounded-lg ${
              snapshotLoading ? 'opacity-50' : ''
            }`}
          />
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center">
            <Move size={18} />
          </div>
          <h3 className="text-base font-semibold text-gray-800">Mover cámara</h3>
        </div>

        <div className="grid grid-cols-3 gap-2 max-w-[220px] mx-auto mb-5">
          {DIRECTIONS.map((entry, index) => {
            if (!entry) {
              return (
                <button
                  key={`refresh-${index}`}
                  type="button"
                  aria-label="Refrescar imagen"
                  title="Refrescar imagen"
                  disabled={snapshotLoading}
                  onClick={fetchSnapshot}
                  className="aspect-square flex items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  <RefreshCw size={18} className={snapshotLoading ? 'animate-spin' : ''} />
                </button>
              );
            }
            const Icon = entry.icon;
            return (
              <button
                key={entry.key}
                type="button"
                aria-label={entry.label}
                onPointerDown={() => handlePointerDown(entry.key)}
                onPointerUp={() => handlePointerStop(entry.key)}
                onPointerLeave={() => handlePointerStop(entry.key)}
                className="aspect-square flex items-center justify-center rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 active:bg-blue-50 active:border-blue-300 transition-colors select-none touch-none"
              >
                <Icon size={18} />
              </button>
            );
          })}
        </div>

        <div>
          <label htmlFor="rop-speed" className="flex items-center justify-between text-sm text-gray-600 mb-1">
            <span>Velocidad</span>
            <span className="font-semibold text-gray-800">{speed}</span>
          </label>
          <input
            id="rop-speed"
            type="range"
            aria-label="Velocidad"
            min={1}
            max={64}
            value={speed}
            onChange={(event) => setSpeed(Number(event.target.value))}
            className="w-full accent-blue-600"
          />
        </div>
      </div>
    </div>
  );
};

export default RopMoverTab;
